import React from 'react';
import {FormattedMessage, injectIntl, IntlShape} from 'react-intl';
import BoxBody from '../common/BoxBody';
import BoxFooter from '../common/BoxFooter';
import XactFooterButton from '../common/XactFooterButton';
import ImpersonationEntry from '../../models/ImpersonationEntry';
import {onEnterKeyPress} from '../../utils/enter-key-listener';
import ImpersonationSettings from '../../models/ImpersonationSettings';
import {ImpersonationChoice} from '../../openam/OpenAmMessage';

interface ImpersonationModeFormProps {
  intl: IntlShape;
  currentEntityOuId: string;
  currentResource: string;
  currentOuId: string;
  entityOuList: ImpersonationEntry[];
  ouList: ImpersonationEntry[];
  userList: ImpersonationEntry[];
  handleImpersonationStep: (settings: ImpersonationSettings) => void;
}

interface ImpersonationModeFormState {
  settings: ImpersonationSettings;
  userInputValue?: string;
  ouInputValue?: string;
}

class ImpersonationModeForm extends React.Component<
  ImpersonationModeFormProps,
  ImpersonationModeFormState
> {
  static MILLISECONDS_TO_WAIT_FROM_LAST_KEY_UP: number = 1000;
  private typingTimeout: any = null;

  constructor(props: ImpersonationModeFormProps) {
    super(props);
    const user =
      props.userList.length > 1 // If there is full list, do not select default user
        ? undefined
        : this.getDefaultImpersonationUser();
    this.state = {
      settings: {
        entityOuId: props.currentEntityOuId,
        resourceId: props.currentResource,
        ouId: props.currentOuId,
        userId: user?.id,
        loadFullUserList: false,
        impersonationChoice: ImpersonationChoice.REFRESH,
      },
      userInputValue: '',
      ouInputValue: '',
    };
  }

  getDefaultImpersonationUser = (): ImpersonationEntry | undefined => {
    return (
      this.props.userList.find(user =>
        user.name.startsWith('Default OU User Impersonation'),
      ) || this.props.userList[0]
    );
  };

  updateSettings = (
    updatedFields: Partial<ImpersonationSettings>,
    callback?: () => void,
  ) => {
    this.setState(
      prevState => ({
        settings: { ...prevState.settings, ...updatedFields },
      }),
      callback,
    );
  };

  componentWillUnmount() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }

  handleEntityOuChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const entityOuName = event.target.value;
    const entityOu = this.props.entityOuList.find(ou => ou.name === entityOuName);
    this.updateSettings(
      {
        entityOuId: entityOu?.id || '',
        resourceId: '',
        ouId: '',
        userId: '',
        loadFullUserList: false,
        impersonationChoice: ImpersonationChoice.REFRESH,
      },
      () => this.props.handleImpersonationStep(this.state.settings),
    );
  };

  handleResourceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const resourceId = event.target.value;
    this.updateSettings({
      resourceId: resourceId,
      ouId: '',
      userId: '',
      loadFullUserList: false,
      impersonationChoice: ImpersonationChoice.REFRESH,
    });
  };

  handleResourceSelected = () => {
    this.props.handleImpersonationStep(this.state.settings);
  };

  handleOuChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const ouName = event.target.value;
    this.setState({ ouInputValue: ouName });
    const ou = this.props.ouList.find(ou => ou.name === ouName);
    this.updateSettings(
      {
        ouId: ou?.id,
        userId: '',
        loadFullUserList: false,
        impersonationChoice: ImpersonationChoice.REFRESH,
      },
      () => !!ou?.id && this.props.handleImpersonationStep(this.state.settings),
    );
  };

  handleUserChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const userName = event.target.value;
    this.setState({ userInputValue: userName });
    const user = this.props.userList.find(user => user.name === userName);
    this.updateSettings({
      userId: user?.id,
    });
  };

  handleLoadFullUserList = () => {
    if (this.props.userList.length === 1 && !this.state.settings.loadFullUserList) {
      this.updateSettings(
        {
          loadFullUserList: true,
          impersonationChoice: ImpersonationChoice.REFRESH,
        },
        () => this.props.handleImpersonationStep(this.state.settings),
      );
    }
  };

  isImpersonationSettingsValid = (): boolean =>
    !!this.state.settings.userId &&
    !!this.state.settings.ouId &&
    !!this.state.settings.entityOuId;

  handleStartImpersonation = () => {
    if (this.isImpersonationSettingsValid()) {
      this.updateSettings(
        {
          impersonationChoice: ImpersonationChoice.IMPERSONATE,
        },
        () => this.props.handleImpersonationStep(this.state.settings),
      );
    }
  };

  handleImpersonationSkip = () => {
    this.updateSettings(
      {
        impersonationChoice: ImpersonationChoice.SKIP,
      },
      () => this.props.handleImpersonationStep(this.state.settings),
    );
  };

  onKeyUpEvent = () => {
    clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => {
      this.handleResourceSelected();
    }, ImpersonationModeForm.MILLISECONDS_TO_WAIT_FROM_LAST_KEY_UP);
  };

  render() {
    const selectedEntityOu = this.props.entityOuList.find(
      ou => ou.id === this.state.settings.entityOuId,
    );
    const selectedOu = this.props.ouList.find(
      ou => ou.id === this.state.settings.ouId,
    );
    const selectedUser = this.props.userList.find(
      user => user.id === this.state.settings.userId,
    );
    return (
      <>
        <BoxBody>
          <h4>
            <FormattedMessage id="impersonation.title" />
          </h4>
          <label className="m-0" htmlFor="entityOu">
            <FormattedMessage id="impersonation.entity.ou.name.label" />
          </label>
          <select
            id="entityOu"
            className="input-field"
            autoComplete="off"
            value={selectedEntityOu?.name}
            onChange={this.handleEntityOuChange}
          >
            {this.props.entityOuList.map(entityOu => (
              <option key={entityOu.id} value={entityOu.name}>
                {entityOu.name}
              </option>
            ))}
          </select>
          <label className="m-0" htmlFor="resource">
            <FormattedMessage id="impersonation.resource.label" />
          </label>
          <input
            type="text"
            className="input-field"
            id="resource"
            onChange={this.handleResourceChange}
            onKeyUp={_ => this.onKeyUpEvent()}
            onKeyDown={event => {
              clearTimeout(this.typingTimeout);
              onEnterKeyPress(event, this.handleResourceSelected);
            }}
            value={this.state.settings.resourceId}
          />
          <label className="m-0" htmlFor="ou">
            <FormattedMessage id="impersonation.ou.name.label" />
          </label>
          <input
            id="ou_name"
            type="text"
            className="input-field"
            list="availableOUs"
            autoComplete="off"
            placeholder="Please select ..."
            value={selectedOu?.name || this.state.ouInputValue}
            onChange={e => {
              this.handleOuChange(e);
            }}
          />
          <datalist className="input-field" id="availableOUs">
            {this.props.ouList.map(ou => (
              <option key={ou.id} value={ou.name}>
                {ou.name}
              </option>
            ))}
          </datalist>
          <label
            className="m-0"
            htmlFor="user"
            title={this.props.intl.formatMessage({
              id: 'impersonation.user.select.info',
              defaultMessage: '',
            })}
          >
            <FormattedMessage id="impersonation.user.name.label" />
          </label>
          <input
            id="user"
            type="text"
            className="input-field"
            list="availableUsers"
            autoComplete="off"
            placeholder="Please select ..."
            disabled={this.props.userList.length === 0}
            value={selectedUser?.name || this.state.userInputValue}
            onChange={e => {
              this.handleUserChange(e);
            }}
            onClick={this.handleLoadFullUserList}
          />
          <datalist className="input-field" id="availableUsers">
            {this.props.userList.map(user => (
              <option key={user.id} value={user.name}>
                {user.name}
              </option>
            ))}
          </datalist>
        </BoxBody>
        <BoxFooter>
          <div className="col-auto mr-auto">
            <div className="row align-items-center">
              <XactFooterButton
                id="skip-impersonation-mode-button"
                onClick={this.handleImpersonationSkip}
                i18nKey="impersonation.skip"
                autoFocus={true}
              />
            </div>
          </div>
          <XactFooterButton
            id="start-impersonation-mode-button"
            onClick={this.handleStartImpersonation}
            i18nKey="impersonation.start"
            disabled={!this.isImpersonationSettingsValid()}
          />
        </BoxFooter>
      </>
    );
  }
}

export default injectIntl(ImpersonationModeForm);
